import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Menu from '../Menu/Menu';
import '../../styles/core.css';
import api from '../../utils/axios.secure';

const ATTENTION_CONTENTS = new Set(['AreaInfo', 'AreaByTime']);
const HEATMAP_AREAS = [
  { id: 4, label: '왼쪽 위' },
  { id: 5, label: '중앙 위' },
  { id: 6, label: '오른쪽 위' },
  { id: 1, label: '왼쪽 아래' },
  { id: 2, label: '중앙 아래' },
  { id: 3, label: '오른쪽 아래' },
];

function unwrapRows(res) {
  const payload = res?.data || res;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload)) return payload;
  return [];
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatNumber(value, digits = 1) {
  const n = toNumber(value);
  if (n === null) return '-';
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(digits);
}

function valueOf(row, key, fallback = '-') {
  const value = row?.[key];
  return value === null || value === undefined || value === '' ? fallback : value;
}

function getPayload(row) {
  return row?.payload && typeof row.payload === 'object' ? row.payload : {};
}

function parseJsonMaybe(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function parseDurationSeconds(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const parts = String(value).split(':');
  if (parts.length !== 3) return Number(value) || 0;

  const hours = Number(parts[0]) || 0;
  const minutes = Number(parts[1]) || 0;
  const seconds = Number(parts[2]) || 0;
  return hours * 3600 + minutes * 60 + seconds;
}

function formatDuration(seconds) {
  const n = toNumber(seconds);
  if (n === null) return '-';
  if (n < 60) return `${n.toFixed(1)}초`;
  const minutes = Math.floor(n / 60);
  const rest = Math.round(n % 60);
  return `${minutes}분 ${rest}초`;
}

function getAttentionType(row) {
  const payload = getPayload(row);
  const explicitType =
    row?.content_name ??
    row?.contentName ??
    row?.AP_contentName ??
    payload?.content_name ??
    payload?.contentName ??
    payload?.AP_contentName ??
    payload?.type ??
    payload?.name ??
    '';

  const normalized = String(explicitType || '').trim();
  if (ATTENTION_CONTENTS.has(normalized)) return normalized;

  if (payload.AreaInfo || payload.areaInfo) return 'AreaInfo';
  if (payload.AreaByTime || payload.areaByTime) return 'AreaByTime';
  return '';
}

function isAttentionRow(row) {
  const payload = getPayload(row);
  return ATTENTION_CONTENTS.has(getAttentionType(row)) || Boolean(payload.AreaInfo || payload.AreaByTime);
}

function getAreaInfo(payload) {
  return parseJsonMaybe(payload.AreaInfo ?? payload.areaInfo);
}

function getAreaByTime(payload) {
  return parseJsonMaybe(payload.AreaByTime ?? payload.areaByTime);
}

function getAreaEntry(areaInfo, displayAreaId) {
  const zeroBased = Object.prototype.hasOwnProperty.call(areaInfo, '0') &&
    !Object.prototype.hasOwnProperty.call(areaInfo, '6');
  const sourceKey = zeroBased ? String(displayAreaId - 1) : String(displayAreaId);
  return areaInfo[sourceKey] || {};
}

function summarizeArea(row) {
  const payload = getPayload(row);
  const areaInfo = getAreaInfo(payload);
  const areaByTime = getAreaByTime(payload);

  const zones = HEATMAP_AREAS.map((area) => {
    const entry = getAreaEntry(areaInfo, area.id);
    const seconds = parseDurationSeconds(entry.TotalTime ?? entry.totalTime ?? entry.time);
    const hitCount = Number(entry.HitCount ?? entry.hitCount ?? entry.count ?? 0) || 0;
    return { ...area, seconds, hitCount };
  });

  const outsideEntry = areaInfo['-1'] || {};
  const offScreenTime = parseDurationSeconds(
    outsideEntry.TotalTime ?? outsideEntry.totalTime ?? outsideEntry.time
  );
  const offScreenCount = Number(
    outsideEntry.HitCount ?? outsideEntry.hitCount ?? outsideEntry.count ?? 0
  ) || 0;

  const onScreenTime = zones.reduce((sum, zone) => sum + zone.seconds, 0);
  const onScreenCount = zones.reduce((sum, zone) => sum + zone.hitCount, 0);
  const totalTime = onScreenTime + offScreenTime;
  const focusRatio = totalTime > 0 ? (onScreenTime / totalTime) * 100 : null;

  const byTimeValues = Object.values(areaByTime);
  const byTimeOffScreenCount = byTimeValues.filter((value) => Number(value) === -1).length;

  return {
    zones,
    offScreenTime,
    offScreenCount,
    onScreenTime,
    onScreenCount,
    focusRatio,
    areaByTime,
    byTimeOffScreenCount,
    byTimeTotalCount: byTimeValues.length,
    maxZoneSeconds: Math.max(...zones.map((zone) => zone.seconds), 0),
  };
}

function summarizeAttention(row) {
  const area = summarizeArea(row);
  return {
    ...row,
    content_name: getAttentionType(row) || row?.content_name || 'AreaInfo',
    ...area,
  };
}

function Detailattentionspan() {
  const { paramsIndex } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const { data: res } = await api.post('/manager/content/contentResult', {});
      const attentionRows = unwrapRows(res)
        .filter(isAttentionRow)
        .map(summarizeAttention);
      const found = attentionRows.find((row) => String(row.idx) === String(paramsIndex));

      setRows(attentionRows);
      setSelected(found || null);
      if (!found) setMessage('선택한 주의 집중 분석 결과를 찾을 수 없습니다.');
    } catch (error) {
      console.error('attention detail error:', error);
      setRows([]);
      setSelected(null);
      setMessage(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          '주의 집중 상세 데이터를 불러오는 중 오류가 발생했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [paramsIndex]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const sameChildRows = useMemo(() => {
    if (!selected) return [];
    return rows.filter((row) => row.user_id === selected.user_id);
  }, [rows, selected]);

  const metricCards = selected
    ? [
        {
          label: '집중률',
          value: selected.focusRatio === null ? '-' : `${formatNumber(selected.focusRatio)}%`,
          hint: '화면 안 / 전체 시간',
        },
        { label: '화면 안', value: formatNumber(selected.onScreenCount, 0), hint: 'HitCount 합계' },
        { label: '화면 밖', value: formatNumber(selected.offScreenCount, 0), hint: 'AreaInfo -1' },
        { label: '응시 시간', value: formatDuration(selected.onScreenTime), hint: '1~6 구역 합계' },
        { label: '이탈 시간', value: formatDuration(selected.offScreenTime), hint: 'AreaInfo -1' },
        { label: '같은 아동 기록', value: sameChildRows.length.toLocaleString(), hint: selected.user_id },
      ]
    : [];

  return (
    <Menu>
      <section className="screen detail-result-screen attention-detail-screen">
        <div className="screen-head">
          <div>
            <p className="eyebrow">Attention Detail</p>
            <h1 className="screen-title">주의 집중 상세</h1>
            <p className="screen-copy">AreaInfo의 -1은 화면 밖, 1~6은 화면 6분할 영역입니다.</p>
          </div>
          <div className="screen-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/attentionspan')}>
              목록으로
            </button>
            <button type="button" className="btn btn-primary" onClick={loadDetail} disabled={loading}>
              새로고침
            </button>
          </div>
        </div>

        {loading && <div className="notice info">상세 데이터를 불러오는 중입니다.</div>}
        {message && !loading && <div className="notice">{message}</div>}

        {selected && (
          <>
            <div className="detail-summary card">
              <div>
                <span className="detail-label">아동 번호</span>
                <strong>{valueOf(selected, 'user_id')}</strong>
              </div>
              <div>
                <span className="detail-label">분석 유형</span>
                <strong>{valueOf(selected, 'content_name')}</strong>
              </div>
              <div>
                <span className="detail-label">모드</span>
                <strong>{valueOf(selected, 'gameMode')}</strong>
              </div>
              <div>
                <span className="detail-label">저장 일시</span>
                <strong>{valueOf(selected, 'saveDate')}</strong>
              </div>
            </div>

            <div className="kpi-grid detail-kpi-grid attention-detail-kpis">
              {metricCards.map((item) => (
                <div className="kpi-card" key={item.label}>
                  <span className="kpi-accent blue" />
                  <p className="kpi-label">{item.label}</p>
                  <strong className="kpi-value">{item.value}</strong>
                  <div className="kpi-meta">
                    <span className="kpi-period">{item.hint}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="detail-grid">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">화면 6분할 히트맵</h2>
                    <p className="card-subtitle">
                      위: 4, 5, 6 / 아래: 1, 2, 3 / 화면 밖: -1
                    </p>
                  </div>
                </div>
                <div className="attention-heatmap">
                  {selected.zones.map((zone) => {
                    const ratio = selected.maxZoneSeconds > 0 ? zone.seconds / selected.maxZoneSeconds : 0;
                    return (
                      <div
                        className="heatmap-cell"
                        key={zone.id}
                        style={{
                          '--heat': ratio,
                        }}
                      >
                        <span>{zone.id}</span>
                        <strong>{zone.label}</strong>
                        <em>{formatDuration(zone.seconds)}</em>
                        <small>{zone.hitCount.toLocaleString()} hits</small>
                      </div>
                    );
                  })}
                </div>
                <div className="offscreen-band">
                  <span>화면 밖 (-1)</span>
                  <strong>{formatDuration(selected.offScreenTime)}</strong>
                  <em>{selected.offScreenCount.toLocaleString()} hits</em>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">원본 payload</h2>
                    <p className="card-subtitle">복호화된 payload 전체입니다.</p>
                  </div>
                </div>
                <pre className="payload-preview">
                  {JSON.stringify(selected.payload || selected, null, 2)}
                </pre>
              </div>
            </div>
          </>
        )}
      </section>
    </Menu>
  );
}

export default Detailattentionspan;
